<script setup lang="ts">
  import { Form } from 'vee-validate'
  import * as yup from 'yup'
  import type { SignupDocuments } from '~/types/apply'
  import cguSchema from '~/utils/schema/cgu-schema'
  import passwordSchema from '~/utils/schema/password-schema'

  const props = defineProps<{
    /** Page the visitor came from; "J'ai déjà un compte" reopens the login there. */
    loginPath: string
  }>()

  const emit = defineEmits<{
    next: [values: SignupDocuments]
    manual: [values: SignupDocuments]
  }>()

  const DOCUMENT_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.oasis.opendocument.text',
    'image/jpeg',
    'image/png'
  ]

  const schema = yup.object({
    cv: yupFile({ types: DOCUMENT_TYPES, maxSize: 5 * 1000 * 1000 })
      .nullable()
      .label('CV'),
    password: passwordSchema(),
    newsletter: yup.boolean(),
    cgu: cguSchema()
  })

  // "Remplir manuellement" skips the analysis, so the CV is only required to run it.
  const manual = ref(false)

  const onSubmit = (values: Record<string, unknown>, { setFieldError }: { setFieldError: (field: string, message: string) => void }) => {
    const casted = schema.cast(values, { stripUnknown: true })
    const isManual = manual.value
    manual.value = false
    if (!isManual && !casted.cv) return setFieldError('cv', 'CV requis')
    const payload: SignupDocuments = { cv: (casted.cv as File) || null, password: casted.password, newsletter: !!casted.newsletter }
    if (isManual) emit('manual', payload)
    else emit('next', payload)
  }
</script>

<template>
  <div class="mx-auto w-full max-w-lg">
    <h1 class="text-center font-special text-3xl font-bold">Création de compte</h1>

    <Form
      class="mt-6 space-y-4"
      v-slot="{ errors, isSubmitting }"
      :initial-values="{ cv: null, newsletter: false, cgu: false }"
      :validation-schema="schema"
      @submit="onSubmit"
      @invalid-submit="manual = false"
    >
      <div>
        <div class="mx-auto w-full max-w-56">
          <FormUploadButton
            name="cv"
            label="+ Importez votre CV"
            button-class="min-h-16"
            :accept="DOCUMENT_TYPES.join(',')"
            :error="errors.cv"
            required
          />
        </div>
        <p class="mt-1 text-center text-xs text-zinc-500">Formats : pdf, word (.doc, .docx), .odt, .jpeg, .png | Taille max : 5 Mo</p>
      </div>

      <div class="-m-2 flex flex-wrap pt-2">
        <FormField
          class="basis-full p-2"
          id="password"
          autocomplete="new-password"
          :error="errors.password"
          name="password"
          placeholder="Mot de passe"
          required
          type="password"
        />
        <span class="flex space-x-4 px-4 pb-2 text-sm text-zinc-500">
          <ul>
            <li>- 12 caractère minimum</li>
            <li>- 1 majuscule</li>
          </ul>
          <ul>
            <li>- 1 chiffre</li>
            <li>- 1 caractère spécial</li>
          </ul>
        </span>
        <FormField
          class="basis-full p-2 pt-4 text-sm"
          id="newsletter"
          label="J’accepte d’être contacté(e) par Profil Public (offre d’emploi, newsletter…)"
          name="newsletter"
          type="checkbox"
          :unchecked-value="false"
          :value="true"
        />
        <div class="flex items-center space-x-2 p-2 text-sm">
          <FormCguCheckbox :error="errors.cgu" />
        </div>
      </div>

      <p class="text-center text-xs italic text-zinc-500">
        En cliquant sur S’inscrire, vous acceptez de pré-remplir automatiquement le formulaire grâce à notre outil assisté par IA. Vous pourrez
        vérifier et modifier les informations avant de valider votre inscription. Sinon, vous pouvez
        <button class="underline duration-300 hover:text-viking-500" type="submit" :disabled="isSubmitting" @click="manual = true">
          Remplir manuellement</button
        >.
      </p>

      <div class="grid grid-rows-2 pt-2 text-center sm:grid-cols-3 sm:grid-rows-1">
        <Button class="sm:col-start-2" :disabled="isSubmitting" type="submit">S’inscrire</Button>
        <NuxtLink
          class="cursor-pointer p-2 text-viking-400 underline duration-300 hover:text-viking-500 sm:ml-auto"
          :to="`${props.loginPath}#connexion`"
          >J'ai déjà un compte</NuxtLink
        >
      </div>
    </Form>
  </div>
</template>
